import type { WrappedResponse } from '../../api/types';

export type SlideKind =
  | 'boarding'
  | 'departure'
  | 'harshness'
  | 'obscurity'
  | 'gem'
  | 'disagreement'
  | 'home'
  | 'drops'
  | 'score_card'
  | 'loved'
  | 'longest'
  | 'skipped'
  | 'formats'
  | 'fingerprint'
  | 'on_board'
  | 'backlog'
  | 'era'
  | 'map'
  | 'ticket';

export interface SlideSpec {
  kind: SlideKind;
  /** What the station's name board says: decorative kanji, then plain English. */
  sign: { kanji: string; en: string };
}

const STOPS: Record<SlideKind, SlideSpec> = {
  boarding: { kind: 'boarding', sign: { kanji: '中央駅', en: 'CENTRAL STATION' } },
  departure: { kind: 'departure', sign: { kanji: '合計', en: 'YOUR TOTALS' } },
  harshness: { kind: 'harshness', sign: { kanji: '採点', en: 'YOUR SCORING' } },
  obscurity: { kind: 'obscurity', sign: { kanji: '秘境', en: 'YOUR TASTE' } },
  gem: { kind: 'gem', sign: { kanji: '穴場', en: 'HIDDEN GEM' } },
  disagreement: { kind: 'disagreement', sign: { kanji: '異論', en: 'YOU DISAGREED' } },
  home: { kind: 'home', sign: { kanji: '故郷', en: 'HOME GENRE' } },
  drops: { kind: 'drops', sign: { kanji: '途中下車', en: 'WHERE YOU QUIT' } },
  score_card: { kind: 'score_card', sign: { kanji: '点数表', en: 'YOUR SCORE CARD' } },
  loved: { kind: 'loved', sign: { kanji: '偏愛', en: 'SOFT SPOT' } },
  longest: { kind: 'longest', sign: { kanji: '長旅', en: 'LONGEST RIDE' } },
  skipped: { kind: 'skipped', sign: { kanji: '未踏', en: 'UNEXPLORED' } },
  formats: { kind: 'formats', sign: { kanji: '車両', en: 'HOW YOU WATCH' } },
  fingerprint: { kind: 'fingerprint', sign: { kanji: '指紋', en: 'YOUR FINGERPRINT' } },
  on_board: { kind: 'on_board', sign: { kanji: '乗車中', en: 'STILL ON BOARD' } },
  backlog: { kind: 'backlog', sign: { kanji: '予定', en: 'YOUR BACKLOG' } },
  era: { kind: 'era', sign: { kanji: '年代', en: 'YOUR ERA' } },
  map: { kind: 'map', sign: { kanji: '現在地', en: 'YOU ARE HERE' } },
  ticket: { kind: 'ticket', sign: { kanji: '終点', en: 'YOUR TICKET' } },
};

/** How many facts the ride stops for, counting the totals and the map. */
export const FACTS = 8;

/** How far a percentile sits from the middle: 0 for typical, 1 at either extreme. */
const ext = (p: number) => Math.min(1, Math.abs(p - 50) / 50);
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

type Fact = Exclude<SlideKind, 'boarding' | 'departure' | 'map' | 'ticket'>;

/**
 * How much each stat has to say about this rider, 0 (they are like everyone)
 * to about 1 (they are nothing like anyone). Null when the list was too thin
 * for it. The weights lean towards stops that name a show, which read better
 * than a second percentile.
 */
function interest(data: WrappedResponse): Partial<Record<Fact, number>> {
  const s: Partial<Record<Fact, number>> = {};
  const { harshness: h, score_card: c, obscurity: o, hidden_gem: g, loved, disagreement: d } = data;
  if (h) s.harshness = 0.95 * ext(h.percentile);
  if (c) s.score_card = 0.8 * ext(c.percentile);
  if (o) s.obscurity = ext(o.percentile);
  if (g) {
    const median = o?.median_members ?? 26692;
    s.gem = 0.15 + 0.85 * clamp01(Math.log10(median / Math.max(1, g.members)) / 1.5);
  }
  if (loved) s.loved = 0.15 + 0.85 * clamp01(loved.gap / 3.5);
  if (d) s.disagreement = 0.15 + 0.85 * clamp01(Math.abs(d.gap) / 3.5) + (d.top100 ? 0.1 : 0);
  if (data.longest) s.longest = 0.8 * (data.longest.percentile / 100) ** 2;
  if (data.home) s.home = 0.1 + 0.8 * ext(data.home.percentile);
  const sk = data.skipped_genre;
  if (sk) s.skipped = 0.75 * clamp01((sk.typical_share - sk.share) / Math.max(sk.typical_share, 0.01));
  if (data.formats) s.formats = 0.6 * ext(data.formats.movie_percentile);
  const lift = Math.max(0, ...(data.fingerprint?.tags ?? []).map((t) => t.lift));
  if (lift) s.fingerprint = 0.1 + 0.85 * clamp01((lift - 1.5) / 3);
  const dr = data.drops;
  if (dr) s.drops = 0.8 * ext(dr.percentile) + (dr.quickest && dr.quickest.episodes <= 2 ? 0.1 : 0);
  const b = data.on_board;
  if (b) s.on_board = 0.5 * ext(b.percentile) + (b.closest ? 0.15 : 0);
  if (data.backlog) s.backlog = 0.7 * ext(data.backlog.percentile);
  if (data.era) s.era = 0.05 + 0.75 * clamp01(Math.abs(data.era.median_year - 2015) / 10);
  return s;
}

/** Stops that tell the same kind of story; a second one from a group has to be that much better. */
const GROUP: Partial<Record<Fact, string>> = {
  harshness: 'scoring',
  score_card: 'scoring',
  gem: 'title',
  loved: 'title',
  disagreement: 'title',
  home: 'genre',
  skipped: 'genre',
  fingerprint: 'genre',
  on_board: 'habit',
  backlog: 'habit',
  drops: 'habit',
};
const REPEAT = 0.7;

/** The spec's order, which is the order the chosen stops are ridden in. */
const ORDER: Fact[] = [
  'harshness', 'score_card', 'obscurity', 'gem', 'loved', 'disagreement', 'longest', 'home',
  'skipped', 'formats', 'fingerprint', 'drops', 'on_board', 'backlog', 'era',
];

/** The most interesting facts about this list, at most `n`, in riding order. */
export function pickFacts(data: WrappedResponse, n: number): Fact[] {
  const score = interest(data);
  const left = ORDER.filter((k) => score[k] !== undefined);
  const chosen: Fact[] = [];
  const used: Record<string, number> = {};
  while (chosen.length < n && left.length) {
    const worth = (k: Fact) => score[k]! * REPEAT ** (used[GROUP[k] ?? k] ?? 0);
    // Ties go to the earlier stop in the spec's order.
    const best = left.reduce((a, k) => (worth(k) > worth(a) ? k : a));
    chosen.push(best);
    left.splice(left.indexOf(best), 1);
    const group = GROUP[best] ?? best;
    used[group] = (used[group] ?? 0) + 1;
  }
  return ORDER.filter((k) => chosen.includes(k));
}

/**
 * The stops, in order: the home station the train leaves from, the totals, the
 * handful of facts that say the most about this list, where the list sits on
 * the map of everyone's taste, and the terminus where the ticket is. A stat the
 * list was too thin for is simply not a stop, so the ride never promises one
 * that is not coming.
 */
export function buildSlides(data: WrappedResponse): SlideSpec[] {
  const map = Boolean(data.taste_map);
  const kinds: SlideKind[] = [
    'boarding',
    'departure',
    ...pickFacts(data, FACTS - 1 - (map ? 1 : 0)),
    ...(map ? (['map'] as const) : []),
    'ticket',
  ];
  return kinds.map((k) => STOPS[k]);
}
